/**
 * 线路择优缓存 · 锁与结果合并存储
 *
 * 单 key（seaart_sdk:wayfinder_<serviceType>）合并「测速锁」与「测速结果」：
 *   { status: 'testing', timestamp }        —— 测速中，timestamp 为心跳时间，每 1000ms 刷新
 *   { status: 'done', line, timestamp }      —— 已完成，timestamp 为完成时间，沿用 4h 过期判定
 *
 * timestamp 单字段双语义，靠 status 区分。
 */

import { cacheStore } from './store'

/** done 结果有效期：4 小时 */
const RESULT_EXPIRY = 4 * 60 * 60 * 1000

export type WayfinderStatus = 'testing' | 'done'

export interface WayfinderCache {
  status: WayfinderStatus
  /** status==='done' 时为最优线路 */
  line?: string
  /** testing：心跳时间戳；done：完成时间戳 */
  timestamp: number
}

/** 拼接线路择优缓存的存储 key */
function cacheKey(key: string): string {
  return `wayfinder_${key}`
}

/** 原始读取缓存（不做任何过期过滤），供锁状态判断使用 */
export function readWayfinderCache(key: string): WayfinderCache | null {
  return cacheStore.getItem<WayfinderCache>(cacheKey(key))
}

/** 写入 / 刷新测速锁心跳：{ status: 'testing', timestamp: now } */
export function writeTestingLock(key: string) {
  cacheStore.setItem(cacheKey(key), { status: 'testing', timestamp: Date.now() } as WayfinderCache)
}

/** 写入测速结果：{ status: 'done', line, timestamp: now } */
export function writeDoneResult(key: string, line: string) {
  cacheStore.setItem(cacheKey(key), { status: 'done', line, timestamp: Date.now() } as WayfinderCache)
}

/** 删除线路择优缓存 */
export function removeWayfinderCache(key: string) {
  cacheStore.removeItem(cacheKey(key))
}

/**
 * 取用有效的 done 结果线路：仅当 status==='done' 且在 4h 内时返回。
 * 注意：过期时会删除缓存（写副作用），故命名为 take 而非 get。
 */
export function takeValidLine(key: string): string | null {
  const data = readWayfinderCache(key)
  if (!data || data.status !== 'done' || !data.line) return null
  if (Date.now() - data.timestamp > RESULT_EXPIRY) {
    removeWayfinderCache(key)
    return null
  }
  return data.line
}
